import React from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "@/components/MarketingHeader";

const SECTIONS = [
  ["purpose", "1. Purpose & Incorporation"],
  ["definitions", "2. Definitions"],
  ["scope", "3. Roles & Scope of Processing"],
  ["posture", "4. Compliance Posture & Boundaries"],
  ["subprocessors", "5. Subprocessors"],
  ["security", "6. Security Measures"],
  ["incident", "7. Security Incident Notification"],
  ["classified", "8. Classified Information Exclusion"],
  ["responsibilities", "9. Customer Responsibilities"],
  ["term", "10. Term & Order of Precedence"],
  ["contact", "11. Contact"],
];

export default function DataProcessingAddendum() {
  return (
    <div className="mkt-page" data-testid="page-dpa">
      <MarketingHeader />

      <section className="mkt-hero">
        <div className="mkt-eyebrow">◢ LEGAL</div>
        <h1 className="mkt-h1">Data Processing &amp; CUI Addendum</h1>
        <p className="legal-updated">Last updated: July 23, 2026</p>
      </section>

      <section className="mkt-body">
        <div className="legal-doc">
          <nav className="legal-toc" aria-label="Table of contents">
            <div className="legal-toc-title">On this page</div>
            <ol>
              {SECTIONS.map(([id, label]) => (
                <li key={id}><a href={`#${id}`}>{label}</a></li>
              ))}
            </ol>
          </nav>

          <p>
            This Data Processing &amp; CUI Addendum ("<strong>Addendum</strong>") supplements, and is
            incorporated by reference into, the <Link to="/terms">Terms of Service</Link> and{" "}
            <Link to="/privacy">Privacy Policy</Link> between Customer and GovCo.AI, LLC. It describes
            how the Service processes Customer Data that includes Controlled Unclassified Information
            ("<strong>CUI</strong>"), and the compliance boundaries that apply to that processing.
            Capitalized terms not defined here have the meaning given in the Terms of Service.
          </p>

          <h2 id="purpose">1. Purpose &amp; Incorporation</h2>
          <p>
            This Addendum applies automatically, without separate signature, to any use of the Service
            involving CUI — consistent with <Link to="/terms">Terms of Service, Section 5</Link>, which
            states the Service is designed for CUI and below. This Addendum does <strong>not</strong>{" "}
            authorize the upload of classified information, Top Secret information, or Special Access
            Program / Special Access Required (SAP/SAR) material; that remains prohibited under all
            circumstances absent a separate, individually negotiated and signed agreement, as described
            in <a href="#classified">Section 8</a>.
          </p>

          <h2 id="definitions">2. Definitions</h2>
          <ul>
            <li>
              "<strong>CUI</strong>" means Controlled Unclassified Information as defined by 32 C.F.R.
              Part 2002 and the National Archives CUI Registry — information the U.S. Government creates
              or possesses, or that an entity creates or possesses for or on behalf of the Government,
              that a law, regulation, or government-wide policy requires or permits safeguarding, but
              which is not classified.
            </li>
            <li>
              "<strong>Subprocessor</strong>" means a third party GovCo.AI engages to process Customer
              Data in order to provide the Service, as listed in <a href="#subprocessors">Section 5</a>.
            </li>
            <li>
              "<strong>Security Incident</strong>" means a confirmed breach of security leading to the
              accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or access
              to, Customer Data transmitted, stored, or otherwise processed by the Service.
            </li>
          </ul>

          <h2 id="scope">3. Roles &amp; Scope of Processing</h2>
          <p>
            Customer determines what Customer Data — including any CUI — it uploads to the Service.
            GovCo.AI processes that data solely as a service provider, to provide the Service's features
            (solicitation ingestion, AI-assisted requirement shredding and drafting, compliance tracking,
            collaboration, and export), and for no other purpose, consistent with{" "}
            <Link to="/terms">Terms of Service, Section 7</Link>.
          </p>

          <h2 id="posture">4. Compliance Posture &amp; Boundaries</h2>
          <p>
            <strong>GovCo.AI does not currently hold FedRAMP authorization, CMMC certification, or a
            completed NIST SP 800-171 System Security Plan assessment.</strong> As stated in{" "}
            <Link to="/terms">Terms of Service, Section 9</Link>, Customer is solely responsible for
            determining and maintaining its own compliance with FAR, DFARS, CMMC, NIST SP 800-171, ITAR,
            EAR, and any other framework applicable to the CUI or export-controlled technical data it
            uploads, and for independently assessing whether the Service's security posture (described
            in <a href="#security">Section 6</a>) is adequate for its specific contractual or regulatory
            obligations before uploading such data. Nothing in this Addendum represents or implies that
            GovCo.AI has been assessed, authorized, or accredited under any such framework.
          </p>

          <h2 id="subprocessors">5. Subprocessors</h2>
          <p>Customer Data that includes CUI may be processed by the following categories of Subprocessor:</p>
          <ul>
            <li><strong>AI processing:</strong> Anthropic (Claude models) — to perform requirement shredding, draft generation, and inline editing assistance on Customer Data you submit to those features.</li>
            <li><strong>Database hosting:</strong> MongoDB Atlas — to store Customer Data and Proposal Assets.</li>
            <li><strong>Application hosting:</strong> Render — to run the Service's backend infrastructure.</li>
            <li><strong>Frontend delivery:</strong> Cloudflare Pages — to serve the Service's web application (does not process Customer Data server-side).</li>
            <li><strong>Transactional email:</strong> Resend — to deliver account and notification emails, which do not include Customer Data beyond what's necessary for the notification itself.</li>
          </ul>
          <p>
            Each Subprocessor is bound by contractual confidentiality and data-protection obligations. We
            will update this list if we add or remove a Subprocessor that processes CUI.
          </p>

          <h2 id="security">6. Security Measures</h2>
          <p>
            The Service applies the following technical and administrative safeguards to Customer Data,
            including CUI: encryption of data in transit; hashed password storage; role-based access
            control, so an Authorized User only sees data their assigned role permits; per-organization
            data isolation, so one Org cannot access another Org's data; and an audit log recording logins,
            edits, approvals, and exports for later review by an Org's Administrator. No system is
            completely secure, and we cannot guarantee the absolute security of Customer Data.
          </p>

          <h2 id="incident">7. Security Incident Notification</h2>
          <p>
            If we confirm a Security Incident affecting an Org's Customer Data, we will notify that Org's
            Administrator(s) without undue delay after confirming the incident, and will provide
            information reasonably available to us about the incident's nature and scope as it becomes
            available.
          </p>

          <h2 id="classified">8. Classified Information Exclusion</h2>
          <p>
            Classified information, Top Secret information, and SAP/SAR material are strictly out of
            scope of the Service and of this Addendum, regardless of any CUI-handling commitments
            described above. Onboarding such material requires a separate, individually negotiated and
            signed agreement between Customer and GovCo.AI, LLC — contact us at{" "}
            <a href="mailto:legal@govco.ai">legal@govco.ai</a> if your organization requires this.
          </p>

          <h2 id="responsibilities">9. Customer Responsibilities</h2>
          <p>Customer is responsible for:</p>
          <ul>
            <li>Marking and handling CUI in accordance with the specific contract, law, or agency policy that designates it as CUI;</li>
            <li>Restricting upload of CUI to Authorized Users who need it for Customer's government-contracting business purposes;</li>
            <li>Promptly notifying GovCo.AI at <a href="mailto:legal@govco.ai">legal@govco.ai</a> of any known or suspected mishandling of CUI within the Service; and</li>
            <li>Independently confirming, before uploading CUI, that the Service's security posture under this Addendum meets the specific requirements of Customer's own government contracts.</li>
          </ul>

          <h2 id="term">10. Term &amp; Order of Precedence</h2>
          <p>
            This Addendum remains in effect for as long as the Terms of Service remain in effect between
            Customer and GovCo.AI. If there is a conflict between this Addendum and the Terms of Service
            or Privacy Policy specifically regarding the processing of CUI, this Addendum controls.
          </p>

          <h2 id="contact">11. Contact</h2>
          <p>
            Questions about this Addendum can be sent to{" "}
            <a href="mailto:legal@govco.ai">legal@govco.ai</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
